import { Download01, XClose } from '@untitledui/icons';
import { useCallback, useState } from 'react';

import { cx } from '@/utils/cx';

import { getFileIcon } from './file-icon';
import { formatFileSize } from './file-utils';

export interface InlineFileCardProps {
  /** Original file name. */
  filename: string;
  /** File size in bytes. */
  size: number;
  /** MIME type. */
  mimeType: string;
  /** URL to the file (for download / image preview). */
  fileUrl?: string;
  /** Called when the download action is triggered. If omitted, the component opens `fileUrl` directly. */
  onDownload?: () => void;
  /**
   * Called when the remove action is triggered. When provided, the trailing
   * button is a remove (×) instead of a download — for pre-send upload strips
   * (e.g. the email composer) where the file isn't downloadable yet.
   */
  onRemove?: () => void;
  /**
   * Visual density. `md` (default) is the two-line card with a 40px icon;
   * `sm` is a compact single-line chip (mail-client style) for surfaces where
   * attachments ride under other content, e.g. email message cards; `xs` is a
   * slim pill for dense contexts (timeline event cards) — it drops the separate
   * download button, so the whole chip is the download/preview click target.
   */
  variant?: 'md' | 'sm' | 'xs';
  /** Additional class name. */
  className?: string;
}

/** Per-variant density classes; md is the original two-line card. */
const VARIANT_CLASSES = {
  md: {
    container: 'gap-2 px-3 py-2 max-w-[280px] rounded-lg',
    thumb: 'size-10',
    iconSize: 40,
    name: 'text-xs font-medium',
    downloadButton: 'p-1.5',
    downloadIcon: 'size-4',
  },
  sm: {
    container: 'gap-1.5 px-2 py-1 max-w-[220px] rounded-md',
    thumb: 'size-5',
    iconSize: 20,
    name: 'text-xs',
    downloadButton: 'p-0.5',
    downloadIcon: 'size-3.5',
  },
  xs: {
    container: 'gap-1 px-1.5 py-0.5 max-w-[180px] rounded-md',
    thumb: 'size-4',
    iconSize: 14,
    name: 'text-[11px]',
    downloadButton: '',
    downloadIcon: '',
  },
} as const;

/**
 * A compact inline file card with optional image thumbnail and download action.
 *
 * Presentational only — supply `fileUrl` for download behavior
 * or `onDownload` for custom handling.
 */
export function InlineFileCard({
  filename,
  size,
  mimeType,
  fileUrl,
  onDownload,
  onRemove,
  variant = 'md',
  className,
}: InlineFileCardProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isImage = mimeType.startsWith('image/');
  const classes = VARIANT_CLASSES[variant];
  const singleLine = variant !== 'md';
  const cardClickable = (isImage && !!fileUrl) || !!onDownload || !!fileUrl;
  // xs has no room for a separate button — the whole chip is the click target
  // (handleCardClick already downloads for non-images / previews images).
  // A remove affordance replaces the download button in every variant. A
  // metadata-only chip (not cardClickable) renders no button at all.
  const showDownloadButton = !onRemove && variant !== 'xs' && cardClickable;
  // The only configuration where the chip itself is the sole action target
  // (no inner button is rendered) — needs keyboard semantics on the container.
  const chipIsSoleTarget = cardClickable && variant === 'xs' && !onRemove;
  const chipActionLabel =
    isImage && fileUrl ? `Preview ${filename}` : `Download ${filename}`;

  const downloadFile = useCallback(() => {
    if (onDownload) {
      onDownload();
    } else if (fileUrl) {
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [onDownload, fileUrl, filename]);

  const handleDownload = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      downloadFile();
    },
    [downloadFile]
  );

  const handleCardClick = useCallback(() => {
    if (isImage && fileUrl) {
      setIsModalOpen(true);
    } else {
      downloadFile();
    }
  }, [isImage, fileUrl, downloadFile]);

  const handleCardKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        if (e.key === ' ') {
          e.preventDefault();
        }
        handleCardClick();
      }
    },
    [handleCardClick]
  );

  const handleRemove = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onRemove?.();
    },
    [onRemove]
  );

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        handleCloseModal();
      }
    },
    [handleCloseModal]
  );

  return (
    <>
      <div
        onClick={cardClickable ? handleCardClick : undefined}
        onKeyDown={chipIsSoleTarget ? handleCardKeyDown : undefined}
        role={chipIsSoleTarget ? 'button' : undefined}
        tabIndex={chipIsSoleTarget ? 0 : undefined}
        aria-label={chipIsSoleTarget ? chipActionLabel : undefined}
        title={
          !showDownloadButton && !onRemove && cardClickable
            ? chipActionLabel
            : undefined
        }
        className={cx(
          'inline-flex items-center',
          classes.container,
          'bg-[var(--color-bg-primary)]',
          'border border-[var(--color-border-secondary)] hover:border-[var(--color-border-primary)]',
          'transition-colors shadow-sm hover:shadow-md',
          cardClickable && 'cursor-pointer',
          className
        )}
      >
        {isImage && fileUrl ? (
          <div
            className={cx(
              'flex-shrink-0 rounded overflow-hidden bg-[var(--color-bg-secondary)] border border-[var(--color-border-secondary)]',
              classes.thumb
            )}
          >
            <img
              src={fileUrl}
              alt={filename}
              className='size-full object-cover'
            />
          </div>
        ) : (
          <div className='flex-shrink-0'>
            {getFileIcon(filename, mimeType, { size: classes.iconSize })}
          </div>
        )}
        {singleLine ? (
          // Single-line: name + size inline, mail-client style.
          <div className='flex min-w-0 flex-1 items-baseline gap-1.5'>
            <span
              className={cx(
                'truncate text-[var(--color-text-primary)]',
                classes.name
              )}
            >
              {filename}
            </span>
            <span className='shrink-0 text-[10px] text-[var(--color-text-quaternary)]'>
              {formatFileSize(size)}
            </span>
          </div>
        ) : (
          <div className='flex-1 min-w-0'>
            <div
              className={cx(
                'truncate text-[var(--color-text-primary)]',
                classes.name
              )}
            >
              {filename}
            </div>
            <div className='text-[10px] text-[var(--color-text-quaternary)]'>
              {formatFileSize(size)}
            </div>
          </div>
        )}
        {onRemove ? (
          <button
            type='button'
            onClick={handleRemove}
            className={cx(
              'flex-shrink-0 text-[var(--color-fg-quaternary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] rounded transition-colors',
              classes.downloadButton || 'p-0.5'
            )}
            aria-label={`Remove ${filename}`}
            title='Remove file'
          >
            <XClose className={classes.downloadIcon || 'size-3'} />
          </button>
        ) : (
          showDownloadButton && (
            <button
              type='button'
              onClick={handleDownload}
              className={cx(
                'flex-shrink-0 text-[var(--color-fg-quaternary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] rounded transition-colors',
                classes.downloadButton
              )}
              title='Download file'
              aria-label={`Download ${filename}`}
            >
              <Download01 className={classes.downloadIcon} />
            </button>
          )
        )}
      </div>

      {/* Image Modal */}
      {isModalOpen && isImage && fileUrl && (
        <div
          className='fixed inset-0 z-50 flex items-center justify-center bg-[var(--color-bg-overlay)]/50 backdrop-blur-sm'
          onClick={handleBackdropClick}
        >
          <div className='relative max-w-7xl max-h-[90vh] m-4'>
            {/* Close button */}
            <button
              onClick={handleCloseModal}
              className='absolute -top-10 right-0 p-2 text-[var(--color-fg-white)] hover:text-[var(--color-fg-white)]/70 transition-colors cursor-pointer'
              aria-label='Close'
            >
              <XClose className='size-6' />
            </button>

            {/* Image */}
            <img
              src={fileUrl}
              alt={filename}
              className='max-w-full max-h-[90vh] rounded-lg shadow-2xl'
            />

            {/* File info bar at bottom */}
            <div className='absolute bottom-0 left-0 right-0 bg-[var(--color-bg-overlay)]/70 text-[var(--color-fg-white)] px-4 py-3 rounded-b-lg'>
              <div className='flex items-center justify-between gap-4'>
                <div className='flex-1 min-w-0'>
                  <p className='text-sm font-medium truncate'>{filename}</p>
                  <p className='text-xs text-[var(--color-fg-white)]/70'>
                    {formatFileSize(size)}
                  </p>
                </div>
                <button
                  onClick={handleDownload}
                  className='flex items-center gap-2 px-3 py-1.5 bg-[var(--color-fg-white)]/10 hover:bg-[var(--color-fg-white)]/20 rounded-md transition-colors text-sm'
                >
                  <Download01 className='size-4' />
                  Download
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
